/**
 * Image upload utilities for browser-safe image handling
 * Converts images to Base64 for API consumption and prevents memory issues
 */

/**
 * Convert image file/URI to Base64 string safely
 * @param {string} uri - Image URI from ImagePicker or file input
 * @returns {Promise<string>} Base64 encoded image data (without data: prefix)
 */
export const convertImageToBase64 = async (uri) => {
  return new Promise((resolve, reject) => {
    try {
      // Create a canvas element to handle the image conversion
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        try {
          // Set canvas dimensions to match image
          canvas.width = img.width;
          canvas.height = img.height;

          // Draw image to canvas
          ctx.drawImage(img, 0, 0);

          // Convert to Base64 (without data:image/...;base64, prefix)
          const dataURL = canvas.toDataURL('image/jpeg', 0.8);
          const base64 = dataURL.split(',')[1];

          // Clean up
          canvas.remove();
          resolve(base64);
        } catch (error) {
          console.error('Error converting image to base64:', error);
          reject(new Error('Failed to convert image to base64'));
        }
      };

      img.onerror = () => {
        console.error('Error loading image:', uri);
        reject(new Error('Failed to load image'));
      };

      // Handle different URI formats
      if (uri.startsWith('blob:') || uri.startsWith('data:')) {
        img.src = uri;
      } else if (uri.startsWith('file://')) {
        // For React Native file URIs, try direct assignment
        img.src = uri;
      } else {
        // For URLs, ensure CORS is handled
        img.crossOrigin = 'anonymous';
        img.src = uri;
      }
    } catch (error) {
      console.error('Error in convertImageToBase64:', error);
      reject(error);
    }
  });
};

/**
 * Validate image file size and type
 * @param {string} uri - Image URI
 * @param {number} maxSizeMB - Maximum file size in MB (default: 2)
 * @param {string[]} allowedTypes - Allowed MIME types
 * @returns {Promise<{valid: boolean, error?: string, size?: number}>}
 */
export const validateImage = async (
  uri,
  maxSizeMB = 2,
  allowedTypes = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp',
  ]
) => {
  try {
    // For blob URIs, we can get size info
    if (uri.startsWith('blob:')) {
      const response = await fetch(uri);
      const blob = await response.blob();
      const fileSizeInMB = blob.size / (1024 * 1024);

      if (!allowedTypes.includes(blob.type)) {
        return {
          valid: false,
          error: 'Unsupported file type. Please upload a valid image.',
        };
      }

      if (fileSizeInMB > maxSizeMB) {
        return {
          valid: false,
          error: `File too big. Please select a file smaller than ${maxSizeMB}MB.`,
          size: fileSizeInMB,
        };
      }

      return { valid: true, size: fileSizeInMB };
    }

    // For other URIs, assume valid (validation will happen on server)
    return { valid: true };
  } catch (error) {
    console.error('Error validating image:', error);
    return {
      valid: false,
      error: 'An error occurred while processing the file. Please try again.',
    };
  }
};

/**
 * Upload business profile image using the dedicated API endpoint
 * @param {string} businessId - Business ID
 * @param {string} imageUri - Image URI from picker
 * @param {string} token - Auth token
 * @param {string} apiBaseUrl - API base URL
 * @returns {Promise<Object>} Upload response
 */
export const uploadBusinessProfileImage = async (
  businessId,
  imageUri,
  token,
  apiBaseUrl
) => {
  try {
    // Validate image first
    const validation = await validateImage(imageUri);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    // Convert to Base64
    const base64Data = await convertImageToBase64(imageUri);

    // Prepare request data matching backend schema
    const requestData = {
      image_data: base64Data,
    };

    // Call the dedicated upload endpoint
    const response = await fetch(
      `${apiBaseUrl}/business/${businessId}/upload-profile-image`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'correlation-id': generateUUID(),
        },
        body: JSON.stringify(requestData),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to upload image');
    }

    return await response.json();
  } catch (error) {
    console.error('Error uploading business profile image:', error);
    throw error;
  }
};

/**
 * Delete business profile image
 * @param {string} businessId - Business ID
 * @param {string} token - Auth token
 * @param {string} apiBaseUrl - API base URL
 * @returns {Promise<Object>} Delete response
 */
export const deleteBusinessProfileImage = async (
  businessId,
  token,
  apiBaseUrl
) => {
  try {
    const response = await fetch(
      `${apiBaseUrl}/business/${businessId}/delete-profile-image`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'correlation-id': generateUUID(),
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to delete image');
    }

    return await response.json();
  } catch (error) {
    console.error('Error deleting business profile image:', error);
    throw error;
  }
};

/**
 * Generate UUID for correlation ID
 * @returns {string} UUID string
 */
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Get image URL from business profile image asset ID
 * @param {string} assetId - Asset ID from backend
 * @param {string} apiBaseUrl - API base URL
 * @returns {string} Full image URL
 */
export const getBusinessProfileImageUrl = (assetId, apiBaseUrl) => {
  if (!assetId) return null;
  return `${apiBaseUrl}/assets/business-profiles/${assetId}`;
};
